import { BaseApiService } from './BaseApiService';

// Production Line interfaces
export type LineType = 'manual' | 'machine';

export interface ProductionLine {
  id: number;
  name: string;
  description?: string;
  defaultWorkers: number;
  lineType: LineType;
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProductionLineData {
  name: string;
  description?: string;
  defaultWorkers?: number;
  lineType?: LineType;
  isActive?: boolean;
  displayOrder?: number;
}

export interface UpdateProductionLineData {
  name?: string;
  description?: string;
  defaultWorkers?: number;
  lineType?: LineType;
  isActive?: boolean;
  displayOrder?: number;
}

export interface ProductionLineWithCapacity extends ProductionLine {
  current_utilization: number;
  total_orders: number;
}

export interface UpdateWorkersData {
  workers: number;
}

class ProductionLineService extends BaseApiService {
  private readonly baseUrl = '/production-lines';

  // Get all production lines
  async getAllProductionLines(): Promise<ProductionLine[]> {
    return this.get<ProductionLine[]>(this.baseUrl);
  }

  // Get active production lines, optionally filtering out lines with inactive schedule overrides
  async getActiveProductionLines(
    startDate?: string,
    endDate?: string,
    excludeScheduleInactive?: boolean
  ): Promise<ProductionLine[]> {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (excludeScheduleInactive) params.set('excludeScheduleInactive', 'true');
    const qs = params.toString();
    return this.get<ProductionLine[]>(`${this.baseUrl}/active${qs ? `?${qs}` : ''}`);
  }

  // Get production lines with workload information, optionally filtering out lines with inactive schedule overrides
  async getProductionLinesWithWorkload(
    startDate?: string,
    endDate?: string
  ): Promise<ProductionLineWithCapacity[]> {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    const qs = params.toString();
    return this.get<ProductionLineWithCapacity[]>(
      `${this.baseUrl}/with-workload${qs ? `?${qs}` : ''}`
    );
  }

  // Get specific production line
  async getProductionLineById(id: number): Promise<ProductionLine> {
    return this.get<ProductionLine>(`${this.baseUrl}/${id}`);
  }

  // Get production line with capacity information
  async getProductionLineCapacity(id: number): Promise<ProductionLineWithCapacity> {
    return this.get<ProductionLineWithCapacity>(`${this.baseUrl}/${id}/capacity`);
  }

  // Create new production line
  async createProductionLine(data: CreateProductionLineData): Promise<ProductionLine> {
    return this.post<ProductionLine>(this.baseUrl, data);
  }

  // Update production line
  async updateProductionLine(id: number, data: UpdateProductionLineData): Promise<ProductionLine> {
    return this.put<ProductionLine>(`${this.baseUrl}/${id}`, data);
  }

  // Delete (deactivate) production line
  async deleteProductionLine(id: number): Promise<{ message: string }> {
    return this.delete<{ message: string }>(`${this.baseUrl}/${id}`);
  }

  // Activate production line
  async activateProductionLine(id: number): Promise<ProductionLine> {
    return this.post<ProductionLine>(`${this.baseUrl}/${id}/activate`, {});
  }

  // Deactivate production line (cascades: unschedules orders on that line)
  async deactivateProductionLine(
    id: number
  ): Promise<ProductionLine & { affectedOrders?: number }> {
    return this.post<ProductionLine & { affectedOrders?: number }>(
      `${this.baseUrl}/${id}/deactivate`,
      {}
    );
  }

  // Get count of active orders on a production line
  async getActiveOrderCount(id: number): Promise<number> {
    const result = await this.get<{ count: number }>(`${this.baseUrl}/${id}/order-count`);
    return result.count;
  }

  // Update line workers
  async updateLineWorkers(id: number, workers: number): Promise<ProductionLine> {
    return this.put<ProductionLine>(`${this.baseUrl}/${id}/workers`, { workers });
  }

  // Helper method to get line options for dropdowns
  async getLineOptions(): Promise<Array<{ value: number; label: string; isActive: boolean }>> {
    const lines = await this.getAllProductionLines();
    return lines.map((line) => ({
      value: line.id,
      label: line.name,
      isActive: line.isActive,
    }));
  }

  // Helper method to get active line options only
  async getActiveLineOptions(): Promise<Array<{ value: number; label: string }>> {
    const lines = await this.getActiveProductionLines();
    return lines.map((line) => ({
      value: line.id,
      label: line.name,
    }));
  }

  // Helper method to validate worker count
  validateWorkerCount(workers: number): boolean {
    return workers > 0 && workers <= 50 && Number.isInteger(workers);
  }

  // Helper method to format capacity percentage
  formatCapacityPercentage(percentage: number): string {
    return `${Math.round(percentage)}%`;
  }

  // Helper method to get capacity status color
  getCapacityStatusColor(percentage: number): 'success' | 'warning' | 'danger' {
    if (percentage < 70) return 'success';
    if (percentage < 90) return 'warning';
    return 'danger';
  }

  // Helper method to format line display name with worker count
  formatLineDisplayName(line: ProductionLine): string {
    return `${line.name} (${line.defaultWorkers} workers)`;
  }
}

// Export singleton instance
export const productionLineService = new ProductionLineService();
export default productionLineService;
